import React, { Fragment, useEffect, useState } from "react";
import { Row, Table, Button, Col } from "reactstrap";

/** Page numbers to render: first, last, current ±1, with "…" gaps. */
const pageWindow = (total, current) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  const set = new Set([0, total - 1, current - 1, current, current + 1].filter((p) => p >= 0 && p < total));
  const sorted = [...set].sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push("gap");
    out.push(p);
  });
  return out;
};

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';

import { rankItem } from '@tanstack/match-sorter-utils';

// Column Filter
const Filter = ({
  column
}) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? '')}
        onChange={value => column.setFilterValue(value)}
        placeholder="Buscar..."
        className="w-36 border shadow rounded"
        list={column.id + 'list'}
      />
      <div className="h-1" />
    </>
  );
};

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <React.Fragment>
      <Col sm={4}>
        <input {...props} value={value} onChange={e => setValue(e.target.value)} />
      </Col>
    </React.Fragment>
  );
};

const TableContainer = ({
  columns,
  data,
  tableClass,
  theadClass,
  divClassName,
  isBordered,
  isPagination,
  isGlobalFilter,
  paginationWrapper,
  SearchPlaceholder,
  pagination,
  buttonClass,
  buttonName,
  isAddButton,
  isCustomPageSize,
  handleUserClick,
}) => {

  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank
    });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    // setPageSize,
    getState
  } = table;

  // useEffect(() => {
  //   Number(customPageSize) && setPageSize(Number(customPageSize));
  // }, [customPageSize, setPageSize]);

  return (
    <Fragment>
      <div className="d-flex flex-column">
      <Row className="mb-2">
        {isCustomPageSize && (
          <Col sm={2}>
            <select
              className="form-select pageSize mb-2"
              aria-label="Itens por página"
              value={table.getState().pagination.pageSize}
              onChange={e => {
                table.setPageSize(Number(e.target.value))
              }}
            >
              {[10, 20, 30, 40, 50].map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  Exibir {pageSize}
                </option>
              ))}
            </select>
          </Col>
        )}

        {isGlobalFilter && <DebouncedInput
          value={globalFilter ?? ''}
          onChange={value => setGlobalFilter(String(value))}
          className="form-control search-box me-2 mb-2 d-inline-block"
          placeholder={SearchPlaceholder}
        />}

        {isAddButton && <Col sm={6}>
          <div className="text-sm-end">
            <Button type="button" className={buttonClass} onClick={handleUserClick}>
              <i className="mdi mdi-plus me-1"></i> {buttonName}</Button>
          </div>
        </Col>}
      </Row>

      <div className={divClassName ? divClassName : "table-responsive"}>
        <Table hover className={tableClass} bordered={isBordered}>
          <thead className={theadClass}>
            {getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      aria-sort={header.column.getIsSorted() === 'asc' ? 'ascending' : header.column.getIsSorted() === 'desc' ? 'descending' : undefined}
                    >
                      {header.isPlaceholder ? null : (
                        <React.Fragment>
                          {header.column.getCanSort() ? (
                            <button
                              type="button"
                              className="btn btn-link p-0 text-reset text-decoration-none fw-semibold d-inline-flex align-items-center"
                              onClick={header.column.getToggleSortingHandler()}
                              aria-label={`Ordenar por ${typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : ''}`.trim()}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              <i
                                className={`ms-1 mdi ${
                                  header.column.getIsSorted() === 'asc' ? 'mdi-arrow-up' :
                                  header.column.getIsSorted() === 'desc' ? 'mdi-arrow-down' : 'mdi-unfold-more-horizontal text-muted'
                                }`}
                                aria-hidden="true"
                              />
                            </button>
                          ) : (
                            flexRender(header.column.columnDef.header, header.getContext())
                          )}
                          {header.column.getCanFilter() ? (
                            <div>
                              <Filter column={header.column} table={table} />
                            </div>
                          ) : null}
                        </React.Fragment>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.map(row => {
              return (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => {
                    return (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>


      {
        isPagination && (
          <Row className="mt-3 align-items-center">
            <Col sm={12} md={5}>
              {(() => {
                const { pageIndex, pageSize } = getState().pagination;
                const total = table.getFilteredRowModel().rows.length;
                const from = total === 0 ? 0 : pageIndex * pageSize + 1;
                const to = Math.min(total, (pageIndex + 1) * pageSize);
                return (
                  <div className="dataTables_info text-muted small" role="status">
                    {total === 0 ? "Nenhum registro" : `Mostrando ${from}–${to} de ${total}`}
                  </div>
                );
              })()}
            </Col>
            <Col sm={12} md={7}>
              <nav className={paginationWrapper} aria-label="Paginação">
                <ul className={pagination}>
                  <li className={`paginate_button page-item previous ${!getCanPreviousPage() ? "disabled" : ""}`}>
                    <button type="button" className="page-link" onClick={previousPage} disabled={!getCanPreviousPage()} aria-label="Página anterior">
                      <i className="mdi mdi-chevron-left" aria-hidden="true"></i>
                    </button>
                  </li>
                  {pageWindow(getPageOptions().length, getState().pagination.pageIndex).map((item, key) => (
                    item === "gap" ? (
                      <li key={`gap-${key}`} className="paginate_button page-item disabled"><span className="page-link">…</span></li>
                    ) : (
                      <li key={item} className={`paginate_button page-item ${getState().pagination.pageIndex === item ? "active" : ""}`}>
                        <button type="button" className="page-link" onClick={() => setPageIndex(item)}
                          aria-label={`Página ${item + 1}`} aria-current={getState().pagination.pageIndex === item ? "page" : undefined}>
                          {item + 1}
                        </button>
                      </li>
                    )
                  ))}
                  <li className={`paginate_button page-item next ${!getCanNextPage() ? "disabled" : ""}`}>
                    <button type="button" className="page-link" onClick={nextPage} disabled={!getCanNextPage()} aria-label="Próxima página">
                      <i className="mdi mdi-chevron-right" aria-hidden="true"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </Col>
          </Row>
        )
      }
      </div>
    </Fragment>
  );
};

export default TableContainer;